import { create } from 'zustand';
import { useAuthStore } from '../auth-store';

/**
 * The session of a cashier who unlocked with their PIN. It is NOT a credential:
 * it opens nothing on the server, and only ever means "use what is on this
 * device". Real tokens stay in memory only, as everywhere else in this app.
 */
export const OFFLINE_TOKEN = 'offline-session';

type OfflineState = {
  /** The server was reachable on the last check. */
  online: boolean;
  /** A pack is on this device, so the till can run with no internet. */
  hasPack: boolean;
  /** When the pack was downloaded. */
  packAt: string | null;
  pending: number;
  /** Refused by the server and waiting on a person. */
  stuck: number;
  syncing: boolean;
  /** Back online, but the session is gone: a cashier must sign in to sync. */
  needsSignIn: boolean;
  /** The browser's install prompt, held until the cashier asks for it. */
  installPrompt: { prompt: () => Promise<unknown> } | null;
  set: (patch: Partial<Omit<OfflineState, 'set'>>) => void;
};

export const useOfflineStore = create<OfflineState>((set) => ({
  online: true,
  hasPack: false,
  packAt: null,
  pending: 0,
  stuck: 0,
  syncing: false,
  needsSignIn: false,
  installPrompt: null,
  set: (patch) => set(patch),
}));

/**
 * Should a request be answered from this device? Only with a pack here, and
 * either the server is out of reach or the cashier unlocked with a PIN (a PIN
 * session can't talk to the server, even once the internet is back).
 */
export function isLocalMode(): boolean {
  const { hasPack, online } = useOfflineStore.getState();
  if (!hasPack) return false;
  return !online || useAuthStore.getState().token === OFFLINE_TOKEN;
}

export function useIsLocalMode(): boolean {
  const hasPack = useOfflineStore((s) => s.hasPack);
  const online = useOfflineStore((s) => s.online);
  const pinSession = useAuthStore((s) => s.token === OFFLINE_TOKEN);
  return hasPack && (!online || pinSession);
}
